/**
 * "You & <name>": one picture and one set of words for two people's stars,
 * shared by the gift room (step 6) and the Community (a seeker's
 * Compatibility). Recipient polish 2026-11-03, DESIGN.md §6, §19, §20.2 and
 * the round 5 block ("DECIDED 2026-11-03"):
 *
 *   - three threads between two columns (YOU · ♓ PISCES | CHRIS LEE · ♌ LEO),
 *     each with ONE whole plain sentence above it, a numbered dot, and a
 *     thickness for how strongly the two parts meet; no labels at the ends;
 *   - the summary paragraph;
 *   - three numbered cards: a plain heading, one everyday picture, "Try this";
 *   - Go deeper, lighter: how you each come across, four themes collapsed,
 *     the full reading collapsed. Open only when both people have a birth
 *     time and place; otherwise one locked line and who has what.
 *
 * `youAndThem(payload, { other })` turns either payload into that view model:
 *   - the BE contract (threads / summary / deep) when it is there;
 *   - today's gift payload (our_stars `reading`: report, my/their_signs,
 *     my/their_moment_known) and today's Community payload
 *     (compatibility: report, aspects, charts, time_warning).
 * With no written threads, the three are picked from the stored contacts the
 * way the writer picks them (§6: the strongest; the strongest easy one not
 * used; the strongest hard one not used) and said in plain words from the
 * pair's own contacts. The meters and the /100 score are never drawn (§19,
 * decision 9).
 *
 * The other person is always the name the context uses ("Chris Lee" as the
 * gift was signed, "Star Gazer" in the Community). Stored words may carry a
 * placeholder ({name}, {other}); it is filled here. No he/she anywhere.
 */

export const THREAD_COLORS = ['#f2a7c3', '#f0d9a3', '#c9a2f2']
export const THREAD_HEADINGS = ['The strongest pull', 'What comes easily', 'Where to go gently']
export const HOWTO = 'Three places your stars meet. The thicker the thread, the stronger it is.'
export const DEEP_CLOSED = 'How you come across, every way your skies touch, the full reading ›'
export const DEEP_LOCKED = 'Opens when you both add a birth time and birthplace.'
export const ASK_SENT = "✓ Asked. You'll both get a message when it's ready."

const EASY = new Set(['conjunction', 'sextile', 'trine'])
const HARD = new Set(['square', 'opposition'])

// The part's plain meaning (§20.2 writer rules), yours and theirs.
const MINE = {
  sun: 'who you are', moon: 'how you feel', mercury: 'how you talk', venus: 'how you love',
  mars: 'what you go after', jupiter: 'where you grow', saturn: 'what you take seriously',
  ascendant: 'how you come across',
}
const theirs = (part, who) => ({
  sun: `who ${who} is`, moon: `how ${who} feels`, mercury: `how ${who} thinks`, venus: `how ${who} loves`,
  mars: `what ${who} goes after`, jupiter: `where ${who} grows`, saturn: `${possessive(who)} careful side`,
  ascendant: `how ${who} comes across`,
}[part] || '')
const VERB = { conjunction: 'joins', sextile: 'gets on easily with', trine: 'flows with', square: 'rubs against', opposition: 'faces' }
const THEME_OF = { venus: 'How you love and feel', moon: 'How you love and feel', mercury: 'How you talk',
  mars: 'How you act', sun: 'How you act', ascendant: 'How you act', jupiter: 'How you grow', saturn: 'How you grow' }
const THEME_ORDER = ['How you love and feel', 'How you talk', 'How you act', 'How you grow']

const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn',
  'Aquarius', 'Pisces']
// Text presentation (U+FE0E): a glyph, never an emoji.
const GLYPHS = ['♈︎', '♉︎', '♊︎', '♋︎', '♌︎', '♍︎', '♎︎', '♏︎', '♐︎', '♑︎', '♒︎', '♓︎']
// How each Rising comes across, in two plain words.
const COMES_ACROSS = {
  Aries: 'direct and quick', Taurus: 'calm and steady', Gemini: 'bright and chatty', Cancer: 'warm and watchful',
  Leo: 'warm and bold', Virgo: 'careful and kind', Libra: 'easy and gracious', Scorpio: 'quiet and intense',
  Sagittarius: 'open and lively', Capricorn: 'composed and capable', Aquarius: 'cool and curious',
  Pisces: 'gentle and dreamy',
}

export function possessive (name = '') {
  const n = String(name || '').trim()
  if (!n) return 'their'
  return /s$/i.test(n) ? `${n}’` : `${n}’s`
}
const cap = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t)

export function signName (s = '') {
  const t = String(s || '').trim().toLowerCase()
  const i = SIGNS.findIndex((x) => x.toLowerCase() === t)
  return i >= 0 ? SIGNS[i] : ''
}
export function signGlyphOf (s = '') {
  const i = SIGNS.indexOf(signName(s))
  return i >= 0 ? GLYPHS[i] : ''
}

/** Fill the other person's name into stored words ({name}, {other}, <name>). */
export function fillName (text = '', who = '') {
  return String(text || '').replace(/\{name\}|\{other\}|<name>/g, who)
}

const strengthOf = (a) => {
  const s = Number(a && (a.strength !== undefined ? a.strength : a.s))
  if (Number.isFinite(s)) return Math.max(0, Math.min(1, s > 1 ? s / 100 : s))
  const orb = Number(a && a.orb)
  return Number.isFinite(orb) ? Math.max(0, Math.min(1, 1 - orb / 8)) : 0.5
}
const natureOf = (a) => {
  const asp = String((a && a.aspect) || '').toLowerCase()
  if (EASY.has(asp)) return 'easy'
  if (HARD.has(asp)) return 'tense'
  const n = String((a && a.nature) || '').toLowerCase()
  return n === 'challenging' || n === 'tense' ? 'tense' : 'easy'
}

/** A contact said plainly: "How you love faces who Chris Lee is". */
export function contactLine (a, who) {
  const mine = MINE[a && a.a]
  const yours = theirs(a && a.b, who)
  const verb = VERB[String((a && a.aspect) || '').toLowerCase()]
  if (!mine || !yours || !verb) return ''
  return `${cap(mine)} ${verb} ${yours}`
}

/**
 * The three threads from the stored contacts (§6): the strongest; the
 * strongest easy one not used; the strongest hard one not used. Contacts
 * that need the viewer's Moon or Rising without a birth time aren't picked.
 */
export function pickThree (aspects = [], { myTime = true } = {}) {
  const list = (Array.isArray(aspects) ? aspects : [])
    .filter((a) => a && MINE[a.a] && theirs(a.b, 'x'))
    .filter((a) => myTime || (a.a !== 'moon' && a.a !== 'ascendant'))
    .slice().sort((x, y) => strengthOf(y) - strengthOf(x))
  const out = []
  if (list[0]) out.push({ a: list[0], kind: 0 })
  const easy = list.find((a) => !out.some((o) => o.a === a) && natureOf(a) === 'easy')
  if (easy) out.push({ a: easy, kind: 1 })
  const hard = list.find((a) => !out.some((o) => o.a === a) && natureOf(a) === 'tense')
  if (hard) out.push({ a: hard, kind: 2 })
  return out
}

const pick = (o, ...keys) => {
  for (const k of keys) if (o && o[k] !== undefined && o[k] !== null && o[k] !== '') return o[k]
  return ''
}

function haveLine (h) {
  if (!h || !h.date) return 'no birthday yet'
  if (h.time && h.place) return 'birthday, birth time and place ✓'
  if (h.time) return 'birthday and birth time'
  if (h.place) return 'birthday and birthplace'
  return 'birthday only'
}

const KIND_INDEX = { pull: 0, easy: 1, gentle: 2 }

/** The BE contract's `you_and` (BE-CONTRACT.md §1), drawn as is. */
function fromContract (y, { other = '', me = '' } = {}) {
  const who = String(y.with_name || '').trim() || String(other || '').trim() || 'them'
  const fill = (t) => fillName(t, who)
  const mySun = signName(y.my_sign)
  const theirSun = signName(y.their_sign)
  const threads = (Array.isArray(y.threads) ? y.threads : []).slice(0, 3).map((t, i) => ({
    n: i + 1,
    kind: t.kind || '',
    sentence: fill(pick(t, 'line', 'sentence')),
    heading: fill(pick(t, 'heading')) || fill(pick(t, 'label')) || THREAD_HEADINGS[KIND_INDEX[t.kind] !== undefined ? KIND_INDEX[t.kind] : i],
    picture: fill(pick(t, 'picture')),
    tryThis: fill(pick(t, 'try', 'try_this')),
    strength: strengthOf(t),
    color: THREAD_COLORS[i],
    nature: t.nature === 'tense' ? 'tense' : 'easy',
  })).filter((t) => t.sentence)
  const d = y.deeper || {}
  const contacts = Array.isArray(d.contacts) ? d.contacts : []
  const themes = (Array.isArray(d.themes) ? d.themes : []).map((th) => {
    const lines = contacts.filter((c) => c.theme === th.key).map((c) => ({
      text: fill(c.line || contactLine({ a: c.mine, b: c.theirs, aspect: c.aspect }, who)),
      strength: strengthOf(c), nature: c.nature === 'tense' ? 'tense' : 'easy',
    })).filter((l) => l.text)
    const n = Number(th.count) || lines.length
    return { title: th.title, lines, tone: th.natures || '', count: `${n} ${n === 1 ? 'way' : 'ways'}${th.natures ? ' · ' + th.natures : ''}` }
  })
  const have = d.have || {}
  const open = !!d.open
  let missing = ''
  if (!open) missing = d.can_add_mine ? (have.them && have.them.time && have.them.place ? 'mine' : 'both') : 'theirs'
  return {
    other: who,
    me: { name: String(me || '').trim(), sun: mySun, glyph: signGlyphOf(mySun) },
    them: { name: who, sun: theirSun, glyph: signGlyphOf(theirSun) },
    howto: y.howto || HOWTO,
    summary: fill(y.summary || ''),
    threads,
    deep: {
      open,
      missing,
      teaser: d.teaser || DEEP_CLOSED,
      lockedLine: fill(d.locked_line || '') || DEEP_LOCKED,
      risings: d.across ? [fill(d.across.mine || ''), fill(d.across.theirs || '')].filter(Boolean) : [],
      themes,
      report: fill(d.full_reading || ''),
      have: { me: haveLine(have.me), them: haveLine(have.them) },
      canAsk: !!d.can_ask_them,
      asked: !!d.asked,
    },
  }
}

/**
 * The view model. `other` is the other person's name as the context says it;
 * `me` the viewer's own name for the pair line (optional).
 */
export function youAndThem (payload, { other = '', me = '' } = {}) {
  const p = payload || {}
  // The contract's one payload, wherever it arrives (reading.you_and,
  // compatibility.you_and, the reveal's you_and), else today's fields.
  const contract = p.you_and || (p.version && (p.threads || p.deeper) ? p : null)
  if (contract) return fromContract(contract, { other, me })
  const who = String(other || '').trim() || String(p.with_name || '').trim() || 'them'
  const charts = p.charts || {}
  const bodySign = (side, part) => signName(charts[side] && charts[side][part] && charts[side][part].sign)
  const mySun = signName(pick(p.my_signs || {}, 'sun')) || bodySign('viewer', 'sun')
  const theirSun = signName(pick(p.their_signs || {}, 'sun')) || bodySign('other', 'sun')
  const myRising = signName(pick(p.my_signs || {}, 'rising', 'ascendant')) || bodySign('viewer', 'ascendant')
  const theirRising = signName(pick(p.their_signs || {}, 'rising', 'ascendant')) || bodySign('other', 'ascendant')

  // Who has what (the contract's `have`, else what today's payloads say).
  const have = p.have || {}
  const mineHave = have.me || have.mine || {
    date: true,
    time: p.my_moment_known !== undefined ? !!p.my_moment_known : !p.time_warning,
    place: p.my_moment_known !== undefined ? !!p.my_moment_known : !p.time_warning,
  }
  const theirHave = have.them || have.theirs || have.other || {
    date: true,
    time: p.their_moment_known !== undefined ? !!p.their_moment_known : !p.time_warning,
    place: p.their_moment_known !== undefined ? !!p.their_moment_known : !p.time_warning,
  }
  const myWhole = !!(mineHave.time && mineHave.place)
  const theirWhole = !!(theirHave.time && theirHave.place)

  // The three threads: written ones when the server sent them, else picked.
  const aspects = Array.isArray(p.aspects) ? p.aspects : (p.deep && Array.isArray(p.deep.contacts) ? p.deep.contacts : [])
  const written = Array.isArray(p.threads) ? p.threads : (Array.isArray(p.lines) ? p.lines : [])
  let threads
  if (written.length) {
    threads = written.slice(0, 3).map((t, i) => ({
      n: i + 1,
      sentence: fillName(pick(t, 'sentence', 'phrase', 'line', 'thread'), who),
      heading: fillName(pick(t, 'heading', 'title', 'parts'), who) || THREAD_HEADINGS[i],
      picture: fillName(pick(t, 'picture', 'pic', 'text', 'body'), who),
      tryThis: fillName(pick(t, 'try_this', 'try', 'tryit', 'try_line'), who),
      strength: strengthOf(t),
      color: THREAD_COLORS[i],
      nature: natureOf(t),
    }))
  } else {
    threads = pickThree(aspects, { myTime: !!mineHave.time }).map((o, i) => {
      const line = contactLine(o.a, who)
      return {
        n: i + 1, sentence: line, heading: THREAD_HEADINGS[o.kind], picture: '', tryThis: '',
        strength: strengthOf(o.a), color: THREAD_COLORS[i], nature: natureOf(o.a),
      }
    })
  }
  threads = threads.filter((t) => t.sentence)

  const summary = fillName(pick(p, 'summary') || pick(p.deep || {}, 'summary') || p.report || '', who)
  const report = fillName(pick(p.deep || {}, 'report', 'full_reading') || pick(p, 'full_reading')
    || (p.summary ? p.report : '') || '', who)

  // Go deeper: four themes, grouped by your part.
  // Decision 10 (2026-11-03): the date-only contacts stay visible without the
  // times; the Moon and Rising ones only once Go deeper is open.
  const deepIn0 = p.deep || {}
  const openNow = deepIn0.open !== undefined ? !!deepIn0.open : (myWhole && theirWhole)
  const TIMED = new Set(['moon', 'ascendant'])
  const groups = {}
  aspects.forEach((a) => {
    if (!openNow && (TIMED.has(a.a) || TIMED.has(a.b))) return
    const line = contactLine(a, who)
    if (!line) return
    const g = THEME_OF[a.a] || 'How you act'
    ;(groups[g] = groups[g] || []).push({ text: line, strength: strengthOf(a), nature: natureOf(a) })
  })
  const tone = (list) => {
    const e = list.filter((l) => l.nature === 'easy').length
    return e === list.length ? 'all easy' : e === 0 ? 'all to go gently with' : 'easy and tense'
  }
  const themes = THEME_ORDER.filter((g) => groups[g]).map((g) => ({
    title: g, lines: groups[g], tone: tone(groups[g]),
    count: `${groups[g].length} ${groups[g].length === 1 ? 'way' : 'ways'} · ${tone(groups[g])}`,
  }))
  const deepIn = p.deep || {}
  const risingMine = fillName(pick(deepIn, 'my_rising_line'), who)
    || (myRising ? `You come across ${COMES_ACROSS[myRising]} (Rising in ${myRising}).` : '')
  const risingTheirs = fillName(pick(deepIn, 'their_rising_line'), who)
    || (theirRising ? `${who} comes across ${COMES_ACROSS[theirRising]} (Rising in ${theirRising}).` : '')

  const open = deepIn.open !== undefined ? !!deepIn.open : deepIn.available !== undefined ? !!deepIn.available
    : (myWhole && theirWhole)
  const missing = open ? '' : (!myWhole && !theirWhole ? 'both' : !myWhole ? 'mine' : 'theirs')

  return {
    other: who,
    me: { name: String(me || '').trim(), sun: mySun, glyph: signGlyphOf(mySun) },
    them: { name: who, sun: theirSun, glyph: signGlyphOf(theirSun) },
    howto: HOWTO,
    summary,
    threads,
    deep: {
      open,
      missing,
      teaser: DEEP_CLOSED,
      lockedLine: DEEP_LOCKED,
      canAsk: missing === 'theirs',
      risings: [risingMine, risingTheirs].filter(Boolean),
      themes,
      report,
      have: { me: haveLine(mineHave), them: haveLine(theirHave) },
      // The other person's time ask, once per pair (§19): already sent?
      asked: !!(deepIn.asked || p.time_asked),
    },
  }
}

/**
 * The three threads' geometry (prototype skyArcs, round 5): the two
 * columns' heads, three gently sagging threads that never cross, and each
 * sentence above its thread with its numbered dot. `narrow` is a phone.
 */
export function threadLayout (threads = [], { narrow = false } = {}) {
  const W = narrow ? 360 : 560
  const H = 300
  const ys = narrow ? [78, 168, 258] : [80, 160, 240]
  const L = narrow ? 22 : 60
  const R = W - L
  const sag = 14
  const f = (n) => Number(n).toFixed(1)
  const list = (Array.isArray(threads) ? threads : []).slice(0, 3)
  const rows = (list.length ? list : [{}, {}, {}]).map((t, i) => {
    const y = ys[i]
    const sentence = t.sentence || ''
    const half = sentence.length * (narrow ? 4.2 : 4.4)
    return {
      ...t,
      y,
      d: `M${L} ${y} Q ${f(W / 2)} ${y + sag} ${R} ${y}`,
      width: f(1.8 + 3.6 * (Number(t.strength) || 0)),
      dot: { x: f(Math.max(14, W / 2 - half - 16)), y: f(y - 17) },
      text: { x: f(W / 2 + 4), y: f(y - 12) },
    }
  })
  return { W, H, L, R, rows }
}
